import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of, forkJoin, Subject } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { PriceAlert, PriceAlertFullInfo, PriceAlertType } from '../models/price-alert';
import { CurrenciesService } from './currencies.service';
import { TickerService } from './ticker.service';
import _keyBy from 'lodash-es/keyBy';
import { ExchangePrice } from '../models/exchange-price';

@Injectable({
  providedIn: 'root'
})
export class PriceAlertService {

  private update = new Subject<PriceAlert>();

  constructor(
    private api: ApiService,
    private currenciesService: CurrenciesService,
    private tickerService: TickerService
  ) { }

  get(): Observable<PriceAlert[]> {
    return this.api.get(`{
      brokerPriceAlerts{
        id
        active
        pair
        minPrice{amount pair}
        maxPrice{amount pair}
        types
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['brokerPriceAlerts']);
      }),
      this.api.unifyErrors()
    );
  }

  getAllPriceAlertInfo(baseCurrency: string): Observable<PriceAlertFullInfo[]> {
    return forkJoin({
      currencies: this.currenciesService.getCryptoWithSupportedBase(baseCurrency),
      tickers: this.tickerService.getAll(),
      alerts: this.get()
    }).pipe(
      map(response => {
        const alerts = _keyBy(response.alerts, val => val.pair);

        return response.currencies.map(currency => {
          const pair = `${currency.code}-${baseCurrency}`;
          return {
            currency: currency,
            ticker: response.tickers[pair] ? response.tickers[pair] : null,
            priceAlert: alerts?.[pair],
          };
        });
      })
    );
  }

  save(minPrice: ExchangePrice, maxPrice: ExchangePrice, types: PriceAlertType[]): Observable<PriceAlert> {
    // setBrokerPriceAlert(minPrice: ExchangePriceInput, maxPrice: ExchangePriceInput, types: [BrokerPriceAlertChannelType!]!)
    return this.api.get(`mutation {
      setBrokerPriceAlert(
        minPrice: {amount: "${minPrice.amount}", pair: "${minPrice.pair}"},
        maxPrice: {amount: "${maxPrice.amount}", pair: "${maxPrice.pair}"},
        types: [${types.join(',')}]
      ){
        ... on BrokerPriceAlert{
          id
          active
          pair
          minPrice{amount pair}
          maxPrice{amount pair}
          types
        }
        ... on OperationError{
          errors{
            field
            message
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['setBrokerPriceAlert'];

        if (data && data['errors']) {
          return throwError(data);
        }

        this.update.next(data);
        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  get changes() { return this.update; }
}
